import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";

const ROLE_ORDER = ["Admin", "Manager", "Coach", "Student"];
const DETAILED_LIST_LIMIT = 5;

function RecipientRoleBlock({ role, people, t }) {
    const shown = people.slice(0, DETAILED_LIST_LIMIT);
    const remaining = people.length - shown.length;
    const roleLabel = t(`settings.holidays.emailPreview.roles.${role}`, { defaultValue: role });

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                {t("settings.holidays.emailPreview.roleBlockTitle", {
                    role: roleLabel,
                    count: people.length,
                })}
            </p>
            {role === "Student" && people.length > DETAILED_LIST_LIMIT ? (
                <>
                    <p className="text-sm text-gray-700 mb-2">
                        {t("settings.holidays.emailPreview.studentBulkNotice", { count: people.length })}
                    </p>
                    <ul className="space-y-1">
                        {shown.map((person) => (
                            <li key={`${role}-${person.email}`} className="text-sm text-gray-600">
                                <span className="font-medium text-gray-800">{person.name}</span>
                                <span className="text-gray-500"> · {person.email}</span>
                            </li>
                        ))}
                    </ul>
                    {remaining > 0 && (
                        <p className="text-xs text-gray-500 mt-2 italic">
                            {t("settings.holidays.emailPreview.moreStudentsHidden", { count: remaining })}
                        </p>
                    )}
                </>
            ) : (
                <ul className="space-y-1">
                    {people.map((person) => (
                        <li key={`${role}-${person.email}`} className="text-sm text-gray-700">
                            <span className="font-medium text-gray-900">{person.name}</span>
                            <span className="text-gray-500"> · {person.email}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function HolidayEmailPreviewModal({
    open,
    preview,
    saving,
    onClose,
    onConfirm,
}) {
    const { t } = useTranslation();

    const groupedRecipients = useMemo(() => {
        const map = {};
        for (const role of ROLE_ORDER) {
            map[role] = [];
        }
        for (const r of preview?.recipients || []) {
            const role = r.role || "Other";
            if (!map[role]) {
                map[role] = [];
            }
            map[role].push(r);
        }
        return map;
    }, [preview]);

    const roleSummary = useMemo(
        () => ROLE_ORDER
            .filter((role) => groupedRecipients[role]?.length)
            .map((role) => {
                const count = groupedRecipients[role].length;
                const roleLabel = t(`settings.holidays.emailPreview.roles.${role}`, { defaultValue: role });
                return t("settings.holidays.emailPreview.roleSummaryItem", {
                    count,
                    role: roleLabel,
                });
            })
            .join(" · "),
        [groupedRecipients, t],
    );

    if (!open || !preview) return null;

    const recipientCount = preview.recipientCount ?? preview.recipients?.length ?? 0;

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="holiday-email-preview-title"
            >
                <div className="px-6 py-5 bg-gradient-to-r from-purple-600 to-indigo-600">
                    <h3 id="holiday-email-preview-title" className="text-xl font-semibold text-white">
                        {t("settings.holidays.emailPreview.title")}
                    </h3>
                    <p className="text-white/90 text-sm mt-1">
                        {t("settings.holidays.emailPreview.subtitle", { count: recipientCount })}
                    </p>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                            {t("settings.holidays.emailPreview.subjectLabel")}
                        </p>
                        <p className="text-sm font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                            {preview.subject}
                        </p>
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                            {t("settings.holidays.emailPreview.previewLabel")}
                        </p>
                        <div className="border border-gray-200 rounded-xl overflow-hidden bg-[#f5f5f5]">
                            <div
                                className="pointer-events-none select-none"
                                dangerouslySetInnerHTML={{ __html: preview.htmlBody }}
                            />
                        </div>
                        <p className="form-helper mt-2">
                            {t("settings.holidays.emailPreview.previewHelper")}
                        </p>
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                            {t("settings.holidays.emailPreview.recipientsLabel", { count: recipientCount })}
                        </p>
                        {recipientCount === 0 ? (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                {t("settings.holidays.emailPreview.noRecipients")}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {roleSummary && (
                                    <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-2.5 text-sm text-purple-900">
                                        {roleSummary}
                                    </div>
                                )}
                                <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                                    {[...ROLE_ORDER, ...Object.keys(groupedRecipients).filter((r) => !ROLE_ORDER.includes(r))]
                                        .filter((role) => groupedRecipients[role]?.length)
                                        .map((role) => (
                                        <RecipientRoleBlock
                                            key={role}
                                            role={role}
                                            people={groupedRecipients[role]}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onConfirm(false)}
                        loading={saving}
                        disabled={saving}
                    >
                        {t("settings.holidays.emailPreview.saveWithoutEmails")}
                    </Button>
                    <Button
                        type="button"
                        variant="gradient-create"
                        onClick={() => onConfirm(true)}
                        loading={saving}
                        disabled={saving || recipientCount === 0}
                    >
                        {t("settings.holidays.emailPreview.saveAndSend")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
